import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useVoice } from '../contexts/VoiceContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Hash, Volume2, Megaphone, ChevronDown, ChevronRight, Plus, Settings, Users, MicOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const channelIcons = {
  text: Hash,
  voice: Volume2,
  announcement: Megaphone,
};

export const ChannelSidebar = ({ onOpenServerSettings }) => {
  const { categories, channels, currentChannel, selectChannel, server, createChannel, createCategory } = useApp();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [collapsedCats, setCollapsedCats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newChName, setNewChName] = useState('');
  const [newChType, setNewChType] = useState('text');
  const [newChCat, setNewChCat] = useState('');
  const [unreads, setUnreads] = useState({});

  const fetchUnreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/channels/unreads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setUnreads(await res.json());
    } catch (e) {}
  }, [token]);

  useEffect(() => {
    fetchUnreads();
    const interval = setInterval(fetchUnreads, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreads]);

  const toggleCategory = (catId) => {
    setCollapsedCats(p => ({ ...p, [catId]: !p[catId] }));
  };

  const handleCreateChannel = async () => {
    if (!newChName.trim()) return;
    await createChannel(newChName.trim(), newChType, newChCat || null, null);
    setNewChName('');
    setShowCreate(false);
  };

  const channelsByCategory = {};
  const uncategorized = [];
  channels.forEach(ch => {
    if (ch.category_id) {
      if (!channelsByCategory[ch.category_id]) channelsByCategory[ch.category_id] = [];
      channelsByCategory[ch.category_id].push(ch);
    } else {
      uncategorized.push(ch);
    }
  });

  return (
    <div className="w-60 h-full bg-[#0e1015] flex flex-col border-r border-white/5" data-testid="channel-sidebar">
      {/* Server header */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-white/5 flex-shrink-0 hover:bg-white/[0.03] cursor-pointer transition-colors" onClick={onOpenServerSettings} data-testid="server-header">
        <h2 className="text-sm font-semibold text-white truncate" data-testid="server-name">
          {server?.name || 'World Gaming'}
        </h2>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-zinc-500 bg-white/5 px-1.5 py-0.5 rounded font-medium">
            {server?.member_count || 0} <Users className="inline h-3 w-3" />
          </span>
          <Settings className="h-3.5 w-3.5 text-zinc-500" />
        </div>
      </div>

      {/* Channels list */}
      <ScrollArea className="flex-1 px-2 py-2">
        {/* Uncategorized channels */}
        {uncategorized.map(ch => (
          <ChannelItem key={ch.channel_id} channel={ch} active={currentChannel?.channel_id === ch.channel_id} unreadCount={unreads[ch.channel_id] || 0} onClick={() => { selectChannel(ch); setUnreads(p => { const n = {...p}; delete n[ch.channel_id]; return n; }); }} />
        ))}

        {/* Categories */}
        {categories.map(cat => (
          <div key={cat.category_id} className="mb-1">
            <button
              onClick={() => toggleCategory(cat.category_id)}
              className="flex items-center gap-1 w-full px-1 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 hover:text-zinc-200 transition-colors"
              data-testid={`category-${cat.category_id}`}
            >
              {collapsedCats[cat.category_id] ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              <span className="truncate">{cat.name}</span>
            </button>

            {!collapsedCats[cat.category_id] && (channelsByCategory[cat.category_id] || []).map(ch => (
              <ChannelItem key={ch.channel_id} channel={ch} active={currentChannel?.channel_id === ch.channel_id} unreadCount={unreads[ch.channel_id] || 0} onClick={() => { selectChannel(ch); setUnreads(p => { const n = {...p}; delete n[ch.channel_id]; return n; }); }} />
            ))}
          </div>
        ))}

        {/* Create channel button */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button
              className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/5 rounded transition-colors mt-2"
              data-testid="create-channel-btn"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('app.createChannel')}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-lg">{t('app.createChannel')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs font-medium text-zinc-400 uppercase">{t('app.channelName')}</label>
                <Input
                  value={newChName} onChange={e => setNewChName(e.target.value)}
                  placeholder="new-channel"
                  className="mt-1 bg-[#0b0d10] border-white/5 text-sm"
                  data-testid="new-channel-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-400 uppercase">{t('app.channelType')}</label>
                <Select value={newChType} onValueChange={setNewChType}>
                  <SelectTrigger className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="new-channel-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141a] border-white/10 text-white">
                    <SelectItem value="text">{t('app.text')}</SelectItem>
                    <SelectItem value="voice">{t('app.voice')}</SelectItem>
                    <SelectItem value="announcement">{t('app.announcement')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-400 uppercase">{t('app.category')}</label>
                <Select value={newChCat} onValueChange={setNewChCat}>
                  <SelectTrigger className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="new-channel-category">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141a] border-white/10 text-white">
                    <SelectItem value="none">None</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreateChannel} className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="create-channel-submit">
                {t('app.createChannel')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </ScrollArea>
    </div>
  );
};

const ChannelItem = ({ channel, active, onClick, unreadCount }) => {
  const Icon = channelIcons[channel.channel_type] || Hash;
  const hasUnread = unreadCount > 0 && !active;
  const { voiceChannel, voiceUsers, speaking, muted, joinVoice, connecting } = useVoice();
  const { user } = useAuth();
  const { selectChannel } = useApp();
  const isVoice = channel.channel_type === 'voice';
  const usersInChannel = isVoice ? voiceUsers.filter(v => v.channel_id === channel.channel_id) : [];
  const isInThisVoice = voiceChannel === channel.channel_id;
  const displayUsers = [...usersInChannel];
  if (isInThisVoice && user && !displayUsers.find(v => v.user_id === user.user_id)) {
    displayUsers.push({
      user_id: user.user_id,
      user,
      channel_id: channel.channel_id,
      muted: muted,
      deafened: false,
    });
  }

  const handleClick = () => {
    if (isVoice) {
      selectChannel(channel);
      if (!isInThisVoice && !connecting) joinVoice(channel.channel_id);
    } else {
      onClick();
    }
  };

  return (
    <div className="mb-0.5">
      <button
        onClick={handleClick}
        data-testid={`channel-${channel.channel_id}`}
        className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm transition-all duration-150 group ${
          active && !isVoice
            ? 'bg-white/10 text-white'
            : isInThisVoice
              ? 'bg-green-500/10 text-green-400'
              : hasUnread
                ? 'text-white font-semibold hover:bg-white/5'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
        }`}
      >
        <Icon className={`h-4 w-4 flex-shrink-0 ${isInThisVoice ? 'text-green-400' : hasUnread ? 'opacity-100' : 'opacity-60'}`} />
        <span className="truncate text-[13px] flex-1 text-left">{channel.name}</span>
        {hasUnread && !isVoice && (
          <span className="h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0" data-testid={`unread-${channel.channel_id}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {isVoice && displayUsers.length > 0 && (
          <span className="text-[10px] text-zinc-500 flex-shrink-0">{displayUsers.length}</span>
        )}
      </button>
      {/* Voice participants */}
      {isVoice && displayUsers.length > 0 && (
        <div className="ml-4 pl-2 border-l border-white/5 space-y-0.5 py-0.5">
          {displayUsers.map(vu => (
            <div key={vu.user_id} className={`flex items-center gap-2 px-1.5 py-1 rounded text-xs ${
              speaking[vu.user_id] ? 'bg-green-500/10' : ''
            }`} data-testid={`voice-user-${vu.user_id}`}>
              <Avatar className={`h-5 w-5 ${speaking[vu.user_id] ? 'ring-2 ring-green-400' : ''}`}>
                <AvatarImage src={vu.user?.avatar} />
                <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-[8px]">
                  {(vu.user?.display_name || vu.user?.username || '?')[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-zinc-300 truncate flex-1">{vu.user?.display_name || vu.user?.username}</span>
              {vu.muted && <MicOff className="h-3 w-3 text-red-400 flex-shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
